import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import {
  SearchIcon,
  FilterIcon,
  SortAscendingIcon,
  SortDescendingIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/outline";
import { 
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ExclamationIcon 
} from "@heroicons/react/solid";

export default function TeamsAppIndex({ userLoggedIn }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [minScore, setMinScore] = useState("");
  const [maxScore, setMaxScore] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState(
    userLoggedIn?.OrganizationUser?.isAppAdmin ? "pinned" : "all"
  );
  const [pinnedApps, setPinnedApps] = useState(new Set()); // Optimistically added
  const [unpinnedApps, setUnpinnedApps] = useState(new Set()); // Optimistically removed

  const isAppAdmin = userLoggedIn?.OrganizationUser?.isAppAdmin;
  const [pinLoadingId, setPinLoadingId] = useState(null);
  const [pinError, setPinError] = useState(null);

  // Fetch All Teams apps with current filters
  const { data: allTeamsApps, isLoading: isLoadingAll, isFetching: isFetchingAll, error: errorAll, refetch: refetchAll } = useQuery(
    ["teams-apps-all", currentPage, limit, searchTerm, selectedCategory, minScore, maxScore, sortBy, sortOrder],
    async () => {
      const token = localStorage.getItem("tokenmob");
      const params = new URLSearchParams({
        page: currentPage,
        limit: limit,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (searchTerm) params.append("search", searchTerm);
      if (selectedCategory) params.append("category", selectedCategory);
      if (minScore) params.append("minScore", minScore);
      if (maxScore) params.append("maxScore", maxScore);

      const response = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/teams-apps?${params}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      return response.data;
    },
    {
      enabled: activeTab === "all",
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
    }
  );

  // Fetch Pinned Teams apps
  const { data: pinnedTeamsApps, isLoading: isLoadingPinned, isFetching: isFetchingPinned, error: errorPinned, refetch: refetchPinned } = useQuery(
    ["teams-apps-pinned", currentPage, limit, searchTerm, selectedCategory, minScore, maxScore, sortBy, sortOrder],
    async () => {
      const token = localStorage.getItem("tokenmob");
      const params = new URLSearchParams({
        page: currentPage,
        limit: limit,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (searchTerm) params.append("search", searchTerm);
      if (selectedCategory) params.append("category", selectedCategory);
      if (minScore) params.append("minScore", minScore);
      if (maxScore) params.append("maxScore", maxScore);

      const response = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/teams-apps/pinned?${params}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      return response.data;
    },
    {
      enabled: activeTab === "pinned" && isAppAdmin,
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
    }
  );

  // Use the appropriate data based on active tab
  const teamsApps = activeTab === "all" ? allTeamsApps : pinnedTeamsApps;
  const isLoading = activeTab === "all" ? isLoadingAll : isLoadingPinned;
  const isFetching = activeTab === "all" ? isFetchingAll : isFetchingPinned;
  const error = activeTab === "all" ? errorAll : errorPinned;

  // Auto scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Get unique categories for filter dropdown
  const { data: categoriesData } = useQuery(
    ["teams-apps-categories"],
    async () => {
      const token = localStorage.getItem("tokenmob");
      const response = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/teams-apps?limit=100`,
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      
      // Extract unique categories
      const allCategories = response.data.apps.flatMap(app => app.categories || []);
      return [...new Set(allCategories)].sort();
    },
    {
      staleTime: 300000, // 5 minutes
    }
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setMinScore("");
    setMaxScore("");
    setSortBy("score");
    setSortOrder("asc");
    setCurrentPage(1);
  };

  const handlePinToggle = async (appId, isPinned) => {
    if (!isAppAdmin || pinLoadingId) return;

    setPinLoadingId(appId);
    setPinError(null);

    // Optimistic UI update - show switch toggle first in Pinned tab, then remove row
    if (isPinned && activeTab === "pinned") {
      // For Pinned tab: delay row removal to show switch animation (400ms)
      setTimeout(() => {
        setUnpinnedApps((prev) => new Set(prev).add(appId));
      }, 400);
      setPinnedApps((prev) => {
        const newSet = new Set(prev);
        newSet.delete(appId);
        return newSet;
      });
    } else if (isPinned) {
      // For All Apps tab: can update immediately
      setUnpinnedApps((prev) => new Set(prev).add(appId));
      setPinnedApps((prev) => {
        const newSet = new Set(prev);
        newSet.delete(appId);
        return newSet;
      });
    } else {
      setPinnedApps((prev) => new Set(prev).add(appId));
      setUnpinnedApps((prev) => {
        const newSet = new Set(prev);
        newSet.delete(appId);
        return newSet;
      });
    }

    try {
      const token = localStorage.getItem("tokenmob");
      if (isPinned) {
        // Unpin the app
        await axios.delete(
          `${process.env.REACT_APP_API_ROOT}/teams-apps/${appId}/pin`,
          {
            headers: {
              Accept: "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );
      } else {
        // Pin the app
        await axios.post(
          `${process.env.REACT_APP_API_ROOT}/teams-apps/${appId}/pin`,
          {},
          {
            headers: {
              Accept: "application/json",
              Authorization: "Bearer " + token,
            },
          }
        );
      }
      // Refetch both lists to keep them in sync
      refetchAll();
      if (isAppAdmin) {
        refetchPinned().then(() => {
          // Clear optimistic states after getting fresh data
          setUnpinnedApps((prev) => {
            const newSet = new Set(prev);
            newSet.delete(appId);
            return newSet;
          });
          setPinnedApps((prev) => {
            const newSet = new Set(prev);
            newSet.delete(appId);
            return newSet;
          });
        });
      }
    } catch (err) {
      console.error("Error toggling pin:", err);
      // Rollback optimistic update on error
      if (isPinned) {
        setUnpinnedApps((prev) => {
          const newSet = new Set(prev);
          newSet.delete(appId);
          return newSet;
        });
      } else {
        setPinnedApps((prev) => {
          const newSet = new Set(prev);
          newSet.delete(appId);
          return newSet;
        });
      }
      const errorMessage = err.response?.data?.error?.code === "NOT_APP_ADMIN"
        ? "You don't have permission to pin/unpin apps"
        : err.response?.data?.error?.code === "APP_NOT_FOUND"
        ? "App not found"
        : err.response?.data?.error?.message || "Failed to update pin status";
      setPinError({ appId, message: errorMessage });
      // Clear error after 5 seconds
      setTimeout(() => setPinError(null), 5000);
    } finally {
      setPinLoadingId(null);
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case "A*":
      case "A":
        return "text-green-600 bg-green-100";
      case "B":
        return "text-yellow-600 bg-yellow-100";
      case "C":
        return "text-orange-600 bg-orange-100";
      case "D":
      case "F":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getScoreIcon = (score) => {
    if (score >= 85) return <ShieldCheckIcon className="h-5 w-5 text-green-500" />;
    if (score >= 65) return <ShieldExclamationIcon className="h-5 w-5 text-yellow-500" />;
    return <ExclamationIcon className="h-5 w-5 text-red-500" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const totalPages = teamsApps ? Math.ceil(teamsApps.total / limit) : 0;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading Teams apps
            </h3>
            <p className="mt-2 text-sm text-red-700">
              {error.response?.data?.error?.message || error.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pin Error Banner */}
      {pinError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationIcon className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{pinError.message}</span>
            </div>
            <button
              onClick={() => setPinError(null)}
              className="text-red-400 hover:text-red-600"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Teams Apps Security</h1>
              <p className="mt-1 text-sm text-gray-500">
                Monitor and analyze security posture of Microsoft Teams applications
              </p>
            </div>
            {teamsApps && (
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  {teamsApps.total}
                </div>
                <div className="text-sm text-gray-500">Total Apps</div>
                <div className="text-sm text-gray-500">
                  Avg Score: {teamsApps.averageScore}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      {isAppAdmin && (
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => {
                  setActiveTab("pinned");
                  setCurrentPage(1);
                }}
                className={`${
                  activeTab === "pinned"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Pinned Apps
              </button>
              <button
                onClick={() => {
                  setActiveTab("all");
                  setCurrentPage(1);
                }}
                className={`${
                  activeTab === "all"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                All Apps
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col space-y-4">
            {/* Search and Filter Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search apps or developers..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FilterIcon className="h-4 w-4 mr-2" />
                  Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">All Categories</option>
                    {categoriesData?.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Score
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={minScore}
                    onChange={(e) => {
                      setMinScore(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Score
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={maxScore}
                    onChange={(e) => {
                      setMaxScore(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Items per page
                  </label>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Apps Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="min-w-full">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-500">Loading Teams apps...</span>
            </div>
          ) : teamsApps?.apps?.length === 0 ? (
            <div className="text-center py-12">
              {activeTab === "pinned" ? (
                <div className="flex flex-col items-center">
                  <svg
                    className="h-12 w-12 text-gray-400 mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                    />
                  </svg>
                  <p className="text-gray-500 text-lg">
                    Open 'All Apps' to select apps to add here
                  </p>
                </div>
              ) : (
                <div className="text-gray-500">No Teams apps found</div>
              )}
            </div>
          ) : (
            <div className="relative">
              {/* Loading Overlay - shown when fetching but not initial load */}
              {isFetching && !isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    <span className="ml-2 text-sm text-gray-600">Updating...</span>
                  </div>
                </div>
              )}

              {/* Table Header */}
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <div className={`grid gap-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isAppAdmin ? 'grid-cols-11' : 'grid-cols-10'}`}>
                  <div className={isAppAdmin ? "col-span-3" : "col-span-4"}>
                    <button
                      onClick={() => handleSort("name")}
                      className="flex items-center space-x-1 hover:text-gray-700"
                    >
                      <span>App</span>
                      {sortBy === "name" && (
                        sortOrder === "asc" ? 
                        <SortAscendingIcon className="h-4 w-4" /> : 
                        <SortDescendingIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <div className="col-span-2">
                    <button
                      onClick={() => handleSort("score")}
                      className="flex items-center space-x-1 hover:text-gray-700"
                    >
                      <span>Security Score</span>
                      {sortBy === "score" && (
                        sortOrder === "asc" ? 
                        <SortAscendingIcon className="h-4 w-4" /> : 
                        <SortDescendingIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <div className="col-span-2">Categories</div>
                  <div className="col-span-2">
                    <button
                      onClick={() => handleSort("lastScanned")}
                      className="flex items-center space-x-1 hover:text-gray-700"
                    >
                      <span>Last Scanned</span>
                      {sortBy === "lastScanned" && (
                        sortOrder === "asc" ? 
                        <SortAscendingIcon className="h-4 w-4" /> : 
                        <SortDescendingIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {isAppAdmin && (
                    <div className="col-span-2 text-center">Pin</div>
                  )}
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-200">
                {teamsApps.apps
                  // Filter out optimistically unpinned apps in pinned tab
                  .filter((app) => activeTab !== "pinned" || !unpinnedApps.has(app.appId))
                  .map((app) => {
                  // Account for optimistic states: pinnedApps adds, unpinnedApps removes
                  const isPinned = (app.isPinned && !unpinnedApps.has(app.appId)) || pinnedApps.has(app.appId);
                  
                  return (
                    <div
                      key={app.appId}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <div className="px-6 py-4">
                        <div className={`grid gap-4 items-center ${isAppAdmin ? 'grid-cols-11' : 'grid-cols-10'}`}>
                          {/* App Info */}
                          <Link
                            to={`/teams-apps/${app.appId}`}
                            className={`${isAppAdmin ? 'col-span-3' : 'col-span-4'} flex items-center space-x-3`}
                          >
                            <img
                              src={app.icon || "/mobstr_logo.png"}
                              alt={`${app.name} icon`}
                              className="h-10 w-10 rounded-lg flex-shrink-0"
                              onError={(e) => {
                                e.target.src = "/mobstr_logo.png";
                              }}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {app.name}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {app.developer}
                              </div>
                            </div>
                          </Link>

                          {/* Security Score */}
                          <Link
                            to={`/teams-apps/${app.appId}`}
                            className="col-span-2 flex items-center space-x-3"
                          >
                            <div className="flex items-center space-x-3">
                              {getScoreIcon(app.overallScore)}
                              <div className="text-center">
                                <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold ${getGradeColor(app.overallGrade)}`}>
                                  {app.overallGrade}
                                </div>
                              </div>
                            </div>
                          </Link>

                          {/* Categories */}
                          <Link
                            to={`/teams-apps/${app.appId}`}
                            className="col-span-2"
                          >
                            <div className="flex flex-wrap gap-1">
                              {app.categories?.slice(0, 2).map((category, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {category}
                                </span>
                              ))}
                              {app.categories?.length > 2 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  +{app.categories.length - 2}
                                </span>
                              )}
                            </div>
                          </Link>

                          {/* Last Scanned */}
                          <Link
                            to={`/teams-apps/${app.appId}`}
                            className="col-span-2 text-sm text-gray-500"
                          >
                            {formatDate(app.lastScanned)}
                          </Link>

                          {/* Pin Toggle Switch */}
                          {isAppAdmin && (
                            <div className="col-span-2 flex flex-col items-center">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handlePinToggle(app.appId, isPinned);
                                }}
                                disabled={pinLoadingId === app.appId}
                                className={`${
                                  isPinned ? "bg-blue-600" : "bg-gray-200"
                                } ${pinLoadingId === app.appId ? "opacity-50 cursor-wait" : "cursor-pointer"} relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                                role="switch"
                                aria-checked={isPinned}
                              >
                                {pinLoadingId === app.appId ? (
                                  <span className="absolute inset-0 flex items-center justify-center">
                                    <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  </span>
                                ) : (
                                  <span
                                    className={`${
                                      isPinned ? "translate-x-5" : "translate-x-0"
                                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                  />
                                )}
                              </button>
                              {pinError?.appId === app.appId && (
                                <span className="text-xs text-red-500 mt-1 max-w-[120px] text-center">
                                  {pinError.message}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {teamsApps && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {(currentPage - 1) * limit + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * limit, teamsApps.total)}
                  </span>{" "}
                  of <span className="font-medium">{teamsApps.total}</span> results
                  {" "}(Page {currentPage} of {totalPages})
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  
                  {/* Page numbers */}
                  {totalPages > 0 && Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
